import type { GetterTree } from 'vuex'
import type { RootState } from '@/store/types'
import type {
  UsersState,
  IUsersLocalMeta,
  TUserAccountsState,
  TReportUserAccountsState,
  TBusinessUserAccountState,
} from '@/store/modules/admin/users/types'
import {
  hasDate,
  hasUserName,
  getFormattedDate,
  getFormattedUserName,
  hasExpiredTrialLicense,
  getFormattedSimpleValue,
  getStatusExpirationDate,
  getFormattedProductName,
  getFormattedReportCloudStorage,
  getFormattedReportLicensedMachines,
} from '@/helpers/formatted-functions'
import { getMaskedPhone } from '@/helpers/mask'
import { isRuCountry } from '@/helpers/company-formatted-functions'

type UsersTree = GetterTree<UsersState, RootState>

export const getters: UsersTree = {
  /**
   * users
   * ? Список пользователей
   *
   * @param {UsersState} state data
   * @returns {TUserAccountsState} список пользователей
   */
  users: (state): TUserAccountsState => {
    return state.users.map((user) => {
      const { email, is_active, created_date } = user

      return {
        ...user,
        hasUserName: hasUserName(user),
        formatUserName: getFormattedUserName(user),
        formatUserEmail: getFormattedSimpleValue(email),
        formatCreatedDate: getFormattedDate(created_date),
        formatIsActive: getFormattedSimpleValue(is_active),
      }
    })
  },

  /**
   * users
   * ? Список пользователей
   *
   * @param {UsersState} state data
   * @returns {TReportUserAccountsState} список пользователей
   */
  reportUsers: (state): TReportUserAccountsState => {
    return state.reportUsers.map((user) => {
      const {
        email,
        is_active,
        sum_orders,
        created_date,
        has_trial_license,
        license_active_count,
        license_expired_count,
        license_max_expired_date,
        last_cloud_quota_usage_date,
      } = user

      return {
        ...user,
        hasUserName: hasUserName(user),
        formatUserName: getFormattedUserName(user),
        userName: getFormattedUserName(user), // Два варианта userName необходимо для отображения в разных модалках (удаление аккаунта и генерация ссылок)
        formatUserEmail: getFormattedSimpleValue(email),
        formatCreatedDate: getFormattedDate(created_date),
        formatIsActive: getFormattedSimpleValue(is_active),
        isExpiredTrialLicense: hasExpiredTrialLicense(user),
        formatSumOrders: getFormattedSimpleValue(sum_orders),
        hasUserBackups: hasDate(last_cloud_quota_usage_date),
        hasMaxExpirationDate: hasDate(license_max_expired_date),
        formatCloudStorage: getFormattedReportCloudStorage(user),
        formatLicensedMachine: getFormattedReportLicensedMachines(user),
        formatHasTrialLicense: getFormattedSimpleValue(has_trial_license),
        formatLastBackupDate: getFormattedDate(last_cloud_quota_usage_date),
        formatMaxExpirationDate: getFormattedDate(license_max_expired_date),
        formatLicenseActiveCount: getFormattedSimpleValue(license_active_count),
        formatLicenseExpiredCount: getFormattedSimpleValue(
          license_expired_count
        ),
        statusExpirationDate: getStatusExpirationDate(license_max_expired_date),
      }
    })
  },

  /**
   * deletedAccountIds
   * ? Список идентификаторов удаленных аккаунтов
   *
   * @param {UsersState} state data
   * @returns {string[]} список идентификаторов удаленных аккаунтов
   */
  deletedAccountIds: (state): string[] => {
    return state.deletedAccountIds
  },

  /**
   * currentReportUsersMeta
   * ? Текущие мета данные списка пользователей
   *
   * @param {UsersState} state data
   * @returns {IUsersLocalMeta} текущие мета данные списка пользователей
   */
  currentReportUsersMeta: (state): IUsersLocalMeta => {
    return state.currentReportUsersMeta
  },

  /**
   * currentReportBusinessUsersMeta
   * ? Текущие мета данные списка бизнес пользователей
   *
   * @param {UsersState} state data
   * @returns {IUsersLocalMeta} текущие мета данные списка бизнес пользователей
   */
  currentReportBusinessUsersMeta: (state): IUsersLocalMeta => {
    return state.currentReportBusinessUsersMeta
  },

  /**
   * currentUsersMeta
   * ? Текущие мета данные списка пользователей для переноса лицензий
   *
   * @param {UsersState} state data
   * @returns {IUsersLocalMeta} текущие мета данные списка пользователей для переноса лицензий
   */
  currentUsersMeta: (state): IUsersLocalMeta => {
    return state.currentUsersMeta
  },

  /**
   * isLoadingUsers
   * ? Процесс загрузки пользователей
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс загрузки пользователей
   */
  isLoadingUsers: (state): boolean => {
    return state.isLoadingUsers
  },

  /**
   * isLoadingReportUsers
   * ? Процесс загрузки отчетов с пользователями
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс загрузки отчетов с пользователями
   */
  isLoadingReportUsers: (state): boolean => {
    return state.isLoadingReportUsers
  },

  /**
   * isLoadingReportBusinessUsers
   * ? Процесс загрузки отчетов по бизнес пользователям
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс загрузки отчетов по бизнес пользователям
   */
  isLoadingReportBusinessUsers: (state): boolean => {
    return state.isLoadingReportBusinessUsers
  },

  /**
   * isLoadingExportBusinessUsers
   * ? Процесс экспорта бизнес пользователей
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс экспорта бизнес пользователей
   */
  isLoadingExportBusinessUsers: (state): boolean => {
    return state.isLoadingExportBusinessUsers
  },

  /**
   * reportBusinessUsers
   * ? Список бизнес пользователей
   *
   * @param {UsersState} state data
   * @returns {boolean} список бизнес пользователей
   */
  reportBusinessUsers: (state): TBusinessUserAccountState[] => {
    return state.reportBusinessUsers.map((item) => {
      const products = (item.products || '')
        .split(',')
        .filter((item) => item.length > 0)
        // TODO: Когда будут возвращаться продукты для бизнес
        // пользователей переделать получение названий продуктов на rootGetters.productName
        .map((product) => getFormattedProductName(product))

      const formattedProducts =
        products.slice(0, 2).join(', ') +
        (products.length > 2 ? ` +${products.length - 2}` : '')

      return {
        ...item,
        license_active_count: item.licenses || item.trial ? 1 : 0, // Необходимо для использования vpc-dropdown-users как для пользователей КБ так и для КБП
        hasUserName: hasUserName(item),
        userName: getFormattedUserName(item),
        formatUserName: getFormattedUserName(item), // Два варианта userName необходимо для отображения в разных модалках (удаление аккаунта и генерация ссылок)
        countryIcon: isRuCountry(item.country) ? 'clr-flag-ru' : 'clr-flag-by',
        formattedUserContacts: [getMaskedPhone(item.phone), item.email]
          .filter((item) => item?.length > 0)
          .join(' • '),
        formattedCreatedDate: getFormattedDate(item.created_date),
        formattedProducts,
      }
    })
  },

  /**
   * isLoadingExportUsers
   * ? Процесс экспорта пользователей
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс экспорта пользователей
   */
  isLoadingExportUsers: (state): boolean => {
    return state.isLoadingExportUsers
  },

  /**
   * isLoadingDeleteAccounts
   * ? Процесс удаления аккаунтов
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс удаления аккаунтов
   */
  isLoadingDeleteAccounts: (state): boolean => {
    return state.isLoadingDeleteAccounts
  },

  /**
   * isLoadingDeleteAllBackups
   * ? Процесс удаления резервных копий
   *
   * @param {UsersState} state data
   * @returns {boolean} процесс удаления резервных копий
   */
  isLoadingDeleteAllBackups: (state): boolean => {
    return state.isLoadingDeleteAllBackups
  },
}
